import { Link } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { DataTableColumnDef } from '@nucleus-commerce/admin-kit/index-table';
import type { AdminAuction } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';

import { AuctionOutcomeBadge, AuctionStatusBadge } from './auction-status-badge';
import { AuctionWhen } from './auction-when';
import { LotThumb } from './lot-thumb';

/** Column model of the auctions table: one tab (status) at a time, newest first. */
export function useAuctionColumns(storeId: string) {
  const { t } = useTranslation('auctions');
  const format = useFormat();

  return useMemo<Array<DataTableColumnDef<AdminAuction>>>(
    () => [
      {
        id: 'lot',
        header: t('columns.lot'),
        cell: ({ row }) => {
          const { lot } = row.original;
          return (
            <span className="flex min-w-0 items-center gap-3">
              <LotThumb lot={lot} />
              <span className="flex min-w-0 flex-col">
                <Link
                  to="/s/$storeId/auctions/$auctionId"
                  params={{ storeId, auctionId: row.original.id }}
                  className="truncate font-medium hover:underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  {lot.title}
                </Link>
                <span className="text-muted-foreground truncate text-xs">
                  {[lot.variantTitle, lot.sku].filter(Boolean).join(' · ') || t('lot.noVariant')}
                </span>
              </span>
            </span>
          );
        },
        meta: { label: t('columns.lot'), lockVisibility: true },
        size: 320,
      },
      {
        id: 'status',
        header: t('columns.status'),
        cell: ({ row }) => <AuctionStatusBadge status={row.original.status} />,
        size: 110,
      },
      {
        id: 'price',
        header: t('columns.price'),
        cell: ({ row }) => {
          const auction = row.original;
          if (auction.hammerPrice) {
            return (
              <span className="flex flex-col items-end tabular-nums">
                <span className="font-medium">{format.money(auction.hammerPrice)}</span>
                <span className="text-muted-foreground text-xs">{t('price.hammer')}</span>
              </span>
            );
          }
          return (
            <span className="flex flex-col items-end tabular-nums">
              <span className="font-medium">
                {format.money(auction.currentPrice ?? auction.startingPrice)}
              </span>
              <span className="text-muted-foreground text-xs">
                {auction.currentPrice ? t('price.current') : t('price.starting')}
              </span>
            </span>
          );
        },
        meta: { label: t('columns.price'), align: 'end' },
        size: 140,
      },
      {
        id: 'bids',
        header: t('columns.bids'),
        cell: ({ row }) => <span className="tabular-nums">{row.original.bidCount}</span>,
        meta: { label: t('columns.bids'), align: 'end' },
        size: 80,
      },
      {
        id: 'bidders',
        header: t('columns.bidders'),
        cell: ({ row }) => <span className="tabular-nums">{row.original.bidderCount}</span>,
        meta: { label: t('columns.bidders'), align: 'end' },
        size: 90,
      },
      {
        id: 'when',
        header: t('columns.when'),
        cell: ({ row }) => <AuctionWhen auction={row.original} />,
        meta: { label: t('columns.when') },
        size: 200,
      },
      {
        id: 'outcome',
        header: t('columns.outcome'),
        cell: ({ row }) =>
          row.original.outcome ? (
            <AuctionOutcomeBadge outcome={row.original.outcome} />
          ) : row.original.status === 'live' ? (
            <span className="text-muted-foreground text-xs">
              {row.original.reserveMet ? t('reserve.met') : t('reserve.notMet')}
            </span>
          ) : (
            <span className="text-muted-foreground">{t('none')}</span>
          ),
        meta: { label: t('columns.outcome') },
        size: 120,
      },
    ],
    [format, storeId, t],
  );
}
